/** Reload a screen when a change touches what it is showing.
 *
 *  The subscription lives once in `App` — one stream, one of the server's
 *  sixteen permits — but only each screen knows its own range, because Week and
 *  Month page independently of the journal's today. So the event is handed down
 *  and the screen decides. That is also what keeps the promise that an
 *  invalidation refetches *only* the affected surface: a task edited in March
 *  costs today nothing.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { affects, type Invalidation, type Surface } from './events';

/** A counter to add to a load effect's dependencies. It increments only when an
 *  event actually touches `surface`. */
export function useRevalidate(invalidation: Invalidation | null, surface: Surface): number {
  const [token, setToken] = useState(0);
  // Events arrive in cursor order. Guarding on it means a re-render that happens
  // to carry the same event cannot trigger a second reload.
  const handled = useRef(-1);

  useEffect(() => {
    if (!invalidation || invalidation.cursor <= handled.current) return;
    handled.current = invalidation.cursor;
    // `surface` is a fresh object every render, so it is deliberately not a
    // dependency: what matters is the range on screen when the event arrived.
    if (affects(invalidation, surface)) setToken((n) => n + 1);
  }, [invalidation]);

  return token;
}
